import React from 'react';
import { ArrowLeft, Palette } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useTheme } from '../context/ThemeContext';
import './AboutPage.css';

const AboutPage = () => {
  const navigate = useNavigate();
  const { currentTheme, cycleTheme, theme } = useTheme();

  // Direct photo references - add/remove based on what you've uploaded
  const photos = [
    '/assets/photos/1.jpg',
    '/assets/photos/2.jpg',
    '/assets/photos/3.jpg',
    '/assets/photos/4.jpg',
    '/assets/photos/5.jpg',
  ];

  return (
    <div className={`transition-all duration-500 ease-in-out ${theme.about.background}
      fixed inset-0 overflow-y-auto w-full                         /* Mobile: Fixed viewport, internal scroll */
      md:relative md:inset-auto md:min-h-screen                    /* Desktop: Normal flow */
    `}>
      {/* Theme Switcher - Top Right */}
      <button
        onClick={cycleTheme}
        className={`fixed top-6 right-6 px-4 py-3 flex items-center gap-3 transition-all duration-300 z-40 font-medium ${theme.about.backButton} ${currentTheme === 'clay' ? 'backdrop-blur-md' : ''}`}
      >
        <Palette size={20} />
        <span className="hidden md:inline">Switch Theme</span>
      </button>

      {/* Prominent Return to Chat Button */}
      <button
        onClick={() => navigate('/')}
        className={`fixed top-6 left-6 px-6 py-3 flex items-center gap-3 transition-all duration-300 z-40 font-medium ${theme.about.backButton} ${currentTheme === 'clay' ? 'backdrop-blur-md' : ''}`}
      >
        <ArrowLeft size={20} />
        <span>Return to Chat</span>
      </button>

      {/* Main Content - Vertical Flow */}
      <div className="flex flex-col items-center w-full min-h-full
        py-24 px-4 space-y-12                                      /* Mobile: Adjusted padding/spacing */
        md:py-20 md:space-y-20                                     /* Desktop: Spacious */
      ">
        {/* Text Content Container */}
        <div className={`w-full max-w-2xl p-6 md:p-8 transition-all duration-500 ${theme.about.container}`}>
          <h1 className={`text-2xl md:text-3xl mb-6 md:mb-8 transition-all duration-500 ${theme.about.title}`}>
            Aayush Panwar
          </h1>

          <div className={`space-y-6 leading-7 transition-all duration-500 ${theme.about.text}`}>
            <p>
              <b>My Story</b>
              <br />
              My initial spark wasn't a cool website, but the darker side of code: cybersecurity. I was in 10th grade, immersed in GTA: San Andreas and Vice City, when the concept of hacking captured my curiosity. By the time college arrived, I had developed a true knack for it—enough to build a mobile app that could access files and cameras (which I quickly tested on my mother's phone). That blend of curiosity and capability defined my early life.
            </p>

            <p>
              I hail from Saharanpur, a town rooted in the land of farmers and Gujjar tradition, where my tech focus often made me feel out of place. This contrast led me to be an aloof personality—deeply fascinated by building things, yet always feeling like an outsider in my immediate environment.
            </p>

            <p>
              Eventually, I grew frustrated with just building in isolation. My biggest human bottleneck is a crippling anxiety when it comes to public speaking, and I struggle to differentiate between genuine and disingenuous people. To fill these gaps, I've immersed myself in books, exploring ideas not just to build better tools, but to better understand people and myself.
            </p>

            <p>
              I channel this drive into my current focus: DevOps and automation consulting. I assist organizations lacking a dedicated DevOps sector, primarily specializing in automation using tools like n8n and AI-powered solutions.
            </p>

            <p>
              I've made countless attempts and projects over the years, though none of them were taken seriously until now. The current project is the one I am truly proud of and determined to scale.
            </p>

            <p>
              Honestly, despite the fast-paced nature of my work, I'm simply seeking a sense of calm in this high-velocity world. Sometimes, I deeply miss the slower rhythm of my village life compared to the constant movement and pressure of metro cities.
            </p>

            <p className="font-medium">
              If you are someone who shares that pursuit of purposeful building alongside inner calm, and you are looking to know more about me, feel free to chat with me.
            </p>
          </div>
        </div>

        {/* Photo Gallery - Separate Section */}
        {photos.length > 0 && (
          <div className="w-full max-w-7xl pb-32 md:pb-0"> {/* Added bottom padding for mobile vertical stack */}
            <div className={`
              flex flex-col items-center px-4                        /* Mobile: Vertical stack, centered */
              md:flex-row md:justify-center md:px-8 md:overflow-visible /* Desktop: Horizontal row, centered */
            `}>
              {photos.map((photo, i) => (
                <img
                  key={i}
                  src={photo}
                  alt={`Photo ${i + 1}`}
                  className={`
                    object-cover rounded-2xl shadow-xl flex-shrink-0
                    w-72 h-96                                        /* Mobile: Larger size */
                    ${i > 0 ? '-mt-64' : ''}                         /* Mobile: Vertical overlap */
                    
                    md:w-64 md:h-80                                  /* Desktop: Standard size */
                    md:mt-0                                          /* Desktop: Reset vertical margin */
                    ${i > 0 ? 'md:-ml-32' : ''}                      /* Desktop: Horizontal overlap */
                    
                    ${i % 2 === 0 ? 'rotate-3' : '-rotate-3'}
                    hover:!z-50 hover:scale-110 hover:!rotate-0
                    transition-all duration-300 cursor-pointer
                  `}
                />
              ))}
            </div>
          </div>
        )}

        {/* Placeholder if no photos */}
        {photos.length === 0 && (
          <div className="w-full max-w-2xl">
            <div className={`p-4 rounded ${currentTheme === 'clay' ? 'bg-white/30' : 'bg-gray-50 border border-gray-200'}`}>
              <p className="text-sm opacity-75">Photos: [upload images to /assets/photos/]</p>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default AboutPage;
